// OrderContext.js
import React, { createContext, useCallback, useContext, useEffect, useMemo, useState, useRef } from 'react';
import { redirect } from 'react-router';
import PropTypes from 'prop-types';
import { apiService } from 'src/utils/apiServices';
import {
  PHYSICAL_API,
  authHeaders,
  emptyToNull,
  physicalErrorMessage,
  withoutEmptyValues,
} from 'src/utils/physical-api';
import { toast } from 'react-toastify';
import { useBoxActions } from './BoxContext';
import { usePalletActions } from './PalleteContext';
import { useShelfActions } from './ShelfContext';

// Helper function to play audio using absolute URL
const playAudio = (soundFile) => {
  try {
    // Use absolute path from domain root for Amplify compatibility
    const audioPath = `${window.location.origin}/sounds/${soundFile}`;
    console.log('Attempting to play audio from:', audioPath);
    const audio = new Audio(audioPath);
    
    audio.onerror = (e) => {
      console.error('Audio file failed to load:', audioPath, e);
    };
    
    audio.play().catch((err) => {
      console.error('Audio play error:', err.name, err.message);
    });
  } catch (error) {
    console.error('Failed to create audio:', error);
  }
};

const OrderContext = createContext();
const OrderActionsContext = createContext();

export const OrderProvider = ({ children }) => {
  const { getBoxes } = useBoxActions();
  const { getShelves } = useShelfActions();
  const { getPalletes } = usePalletActions();
  const [orders, setOrders] = useState([]);
  const [ordersCheck, setOrdersCheck] = useState([]);

  const [ordersTimeSlot, setOrdersTimeSlot] = useState([]);
  const [orderRecordDetails, setOrderRecordDetails] = useState({});

  const [physicalOrders, setPhysicalOrders] = useState({});
  const [physicalOrdersDetails, setPhysicalOrdersDetails] = useState({});
  const [physicalCheckoutOrders, setPhysicalCheckoutOrders] = useState([]);
  const [course, setCourse] = useState([]);
  const lastOrdersFiltersRef = useRef({});
  const lastOrdersCheckTypeRef = useRef('ONLINE');

  const getOrderss = async (filters = {}) => {
    // Remove 'role' from filters if it is an empty array
    const effectiveFilters = Object.entries(filters).reduce((acc, [key, value]) => {
      if (key === 'role' && Array.isArray(value) && value.length === 0) {
        return acc; // skip adding to the accumulator
      }
      acc[key] = value; // otherwise add to the accumulator
      return acc;
    }, {});

    lastOrdersFiltersRef.current = effectiveFilters;

    const query = new URLSearchParams(effectiveFilters).toString();

    try {
      const response = await apiService.get(`/orders?${query}`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
      });
      const data = await response.data.orders;
      setOrderRecordDetails({
        page: response.data.page,
        take: response.data.take,
        total: response.data.total,
        totalPages: Math.ceil(+response.data.total / +response.data.take),
      });
      setOrders(data);
    } catch (error) {
      if (error.response && error.response.status === 401) {
        localStorage.removeItem('token');
        setTimeout(() => {
          window.location.href = '/auth/jwt/login';
        }, 2000);
      }
    }
  };

  const getOrderssPhysical = async (filters = {}) => {
    const query = new URLSearchParams(withoutEmptyValues(filters)).toString();

    try {
      const response = await apiService.get(`${PHYSICAL_API.orders}?${query}`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
      });
      const data = await response.data;
      setPhysicalOrdersDetails({
        page: data.page,
        take: data.take,
        total: data.total,
        totalPages: Math.ceil(+data.total / +data.take),
      });
      setPhysicalOrders(data.data);
    } catch (error) {
      if (error.response && error.response.status === 401) {
        localStorage.removeItem('token');
        setTimeout(() => {
          window.location.href = '/auth/jwt/login';
        }, 2000);
      }
    }
  };

  const getOrdersForCheck = async (type = lastOrdersCheckTypeRef.current) => {
    try {
      const url = type === 'ONLINE' ? `/orders/check` : PHYSICAL_API.ordersCheckedByMe;
      lastOrdersCheckTypeRef.current = type;
      const response = await apiService.get(url, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
      });
      const data = await response.data;
      setOrdersCheck(data);
      return data;
    } catch (error) {
      setOrdersCheck([]);
      return undefined;
    }
  };

  const getOrderTimeSlot = async (type = 'ONLINE') => {
    try {
      const url = type === 'ONLINE' ? '/orders/timeSlot' : PHYSICAL_API.ordersDeliverySlots;
      const response = await apiService.get(url, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
      });
      const data = await response.data;
      setOrdersTimeSlot(data);
      return data;
    } catch (error) {
      setOrdersCheck([]);
      return undefined;
    }
  };

  const getPhysicalOrdersByGroupId = async (trackingNumber) => {
    if (!trackingNumber || !String(trackingNumber).trim()) {
      setPhysicalCheckoutOrders([]);
      return [];
    }
    const encoded = encodeURIComponent(String(trackingNumber).trim());
    try {
      const { data } = await apiService.get(`${PHYSICAL_API.ordersPickupByTracking}/${encoded}`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
      });

      setPhysicalCheckoutOrders(data);
      return data;
    } catch (error) {
      console.error('Error fetching orders:', error);
      throw error;
    }
  };

  const getOrdersByReceiverPhone = async (phoneNumber) => {
    if (!phoneNumber || !String(phoneNumber).trim()) {
      setPhysicalCheckoutOrders([]);
      return [];
    }
    const encoded = encodeURIComponent(String(phoneNumber).trim());
    try {
      const { data } = await apiService.get(`${PHYSICAL_API.ordersPickupByPhone}/${encoded}`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
      });
      setPhysicalCheckoutOrders(data);
      return data;
    } catch (error) {
      console.error('Error fetching orders:', error);
      throw error;
    }
  };

  const getOrderById = async (id) => {
    try {
      const response = await apiService.get(`/orders/${id}`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
      });
      const data = await response.data;
      return data;
    } catch (error) {
      console.error('Error fetching order:', error);
      throw error;
    }
  };

  const getPhysicalOrderById = async (id) => {
    const response = await apiService.get(`${PHYSICAL_API.orders}/${id}`, {
      headers: {
        Authorization: `Bearer ${localStorage.getItem('token')}`,
      },
    });
    return response.data;
  };

  const createOrder = async (newOrder) => {
    try {
      const response = await apiService.post('/orders', newOrder, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
      });
      const data = await response.data;
      toast.success('Order created!');
      setOrders((prevOrderss) => [...prevOrderss, data]);
    } catch (error) {
      toast.error('Error creating order!');
    }
  };

  const deleteOrder = async (orderId) => {
    try {
      await apiService.put(
        '/orders/update/soft',
        { ids: orderId },
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`,
          },
        }
      );
      getOrderss();
      getOrderssPhysical();
      toast.success('Order deleted!');
    } catch (error) {
      toast.error('Error deleting order!');
    }
  };

  const updateOrder = async (orderId, updatedOrder) => {
    try {
      const response = await apiService.put(
        `/orders/updateOrderFromDashboard/${orderId}`,
        updatedOrder,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`,
          },
        }
      );
      // eslint-disable-next-line
      const data = await response.data;
      toast.success('Order updated!');
      await getOrderss(lastOrdersFiltersRef.current);
      return data;
    } catch (error) {
      toast.error('Error updating order!');
      throw error;
    }
  };

  const checkAndDeliverOrder = async (orderId, updateDto) => {
    try {
      const response = await apiService.put(`/orders/checkAndDeliver/${orderId}`, updateDto, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
      });
      const data = await response.data;
      toast.success('Order updated!');
      await getOrderss(lastOrdersFiltersRef.current);
      return data;
    } catch (error) {
      toast.error('Error updating order!');
      throw error;
    }
  };
  const updateDeclaration = async (id, updateDeclarationDto) => {
    try {
      const response = await apiService.put(`declarations/dashboard/${id}`, updateDeclarationDto, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
      });
      // eslint-disable-next-line
      const data = await response.data;
      toast.success('Declaration updated!');
      // Add any additional actions if needed, such as refreshing the data
    } catch (error) {
      toast.error('Error updating declaration!');
    }
  };

  const createDeclarationDB = async (createDeclarationDto, length) => {
    try {
      const response = await apiService.post(
        `declarations/createFromDashboard`,
        createDeclarationDto,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`,
          },
        }
      );
      // eslint-disable-next-line
      const data = response.data;
      if (data && !length) {
        updateOrder(createDeclarationDto.order_id, { declaration_status: 'DECLERED' });
      }
      toast.success('Declaration Created!');
      return data;
    } catch (error) {
      toast.error('Error Creating declaration!');
      throw error;
    }
  };

  const updateOrderByBoxAndPallet = async (updateOrderData) => {
    try {
      const response = await apiService.patch(
        `/orders/update-by-tracking-and-box-pallet`,
        updateOrderData,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`,
          },
        }
      );
      const data = await response.data;
      if (data) {
        getOrderss();
        getBoxes();
        getPalletes();
        toast.success('Order Updated!');
        return data;
      }
    } catch (error) {
      toast.error(error?.response?.data?.error);
    }
    return undefined;
  };

  const updateOrderByShelf = async (updateOrderData) => {
    try {
      const response = await apiService.patch(`/orders/update-by-tracking-shelf`, updateOrderData, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
      });
      const data = await response.data;
      if (data) {
        getOrderss();
        getShelves();
        getPalletes();
        toast.success('Order Updated!');
        playAudio('success.wav');
        return data;
      }
    } catch (error) {
      toast.error(error.response.data.message || error.response.data.error);
        playAudio('error.wav');
    }
    return undefined;
  };

  const updateOrderStatus = async (updateOrderData) => {
    try {
      const response = await apiService.patch(`/orders/update-order-status`, updateOrderData, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
      });
      const data = await response.data;
      if (data) {
        getOrderss();
        getOrdersForCheck();
        getOrderTimeSlot();
        toast.success('Order Updated!');
        return data;
      }
    } catch (error) {
      toast.error(error.response.data.error);
    }
    return undefined;
  };

  const sendToDelivery = async (orderIds, userToken) => {
    try {
      await apiService.put(
        'orders/update/status/out-for-delivery',
        {
          ids: orderIds,
        },
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`,
          },
        }
      );
      toast.success('Order updated!');
      getOrdersForCheck();
      getOrderss();
      return true;
    } catch (error) {
      toast.error('Error updating orders!');
      return false;
    }
  };

  const rejectPayment = async (id) => {
    try {
      await apiService.post(
        'transactions/rejectPayment',
        {
          id,
        },
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`,
          },
        }
      );
      toast.success('payment updated!');
      return true;
    } catch (error) {
      toast.error('Error updating payment!');
      return false;
    }
  };

  /// order creation flow
  const getOrderByTracking = async (tracking) => {
    try {
      const response = await apiService.get(`orders/search/trackingNumber/${tracking}`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
      });
      // eslint-disable-next-line
      const data = response.data;
      toast.success('Order Found');
      return data;
    } catch (error) {
      toast.warn('Order Not Found');
      throw error;
    }
  };

  /// order creation flow
  const createOrderWithSteps = async (orderData) => {
    // orderData.branch_id = '1ad52c30-2978-4fe0-9db4-ecbba1e04365';
    try {
      const response = await apiService.patch(`orders/create-order-by-steps`, orderData, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
      });
      // eslint-disable-next-line
      const data = response.data;
      toast.success('Order Created');
      getOrderss();
      return data;
    } catch (error) {

      toast.warn('Order Not Created');
    }
    return null;
  };

  const updateGroup = async (groupId, payload) => {
    try {
      const res = await apiService.patch(`${PHYSICAL_API.groups}/${groupId}`, payload, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
      });

      toast.success('Group updated');
      return res.data;
    } catch (error) {
      const msg = error?.response?.data?.message || error?.message || 'Failed to update group';
      toast.error(Array.isArray(msg) ? msg.join(', ') : msg);
      return null;
    }
  };

  const getCustomFeeSum = async (groupId) => {
    try {
      const res = await apiService.get(`${PHYSICAL_API.groups}/${groupId}/custom-fee-sum`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
      });

      return res.data;
    } catch (error) {
      const msg = error?.response?.data?.message || error?.message || 'Failed to get custom fee sum';
      toast.error(Array.isArray(msg) ? msg.join(', ') : msg);
      return null;
    }
  };

  const payCustomFees = async (groupId) => {
    try {
      const res = await apiService.patch(`${PHYSICAL_API.groups}/${groupId}/pay-custom-fees`, {}, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
      });

      toast.success('Custom fees paid successfully');
      return res.data;
    } catch (error) {
      const msg = error?.response?.data?.message || error?.message || 'Failed to pay custom fees';
      toast.error(Array.isArray(msg) ? msg.join(', ') : msg);
      return null;
    }
  };

  const getQuickshipperExcelData = async () => {
    try {
      const response = await apiService.get('/quickshipper/excel-upload', {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
      });
      return response.data;
    } catch (error) {
      console.error('Error fetching quickshipper Excel data:', error);
      if (error.response && error.response.status === 401) {
        localStorage.removeItem('token');
        redirect('/');
      }
      toast.error('Failed to fetch quickshipper data');
      return null;
    }
  };

  /// order creation flow – accepts single order or array of orders; sends { orders: [...] } to API
  const createPhysicalOrder = async (orderData, direction, groupFields = {}) => {
    const newOrders = (Array.isArray(orderData) ? orderData : [orderData]).map(
      withoutEmptyValues
    );
    try {
      const response = await apiService.post(
        PHYSICAL_API.orders,
        withoutEmptyValues({ direction, ...groupFields, orders: newOrders }),
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`,
          },
        }
      );
      // eslint-disable-next-line
      const data = response.data;
      toast.success('Group Created');
      getOrderss();
      return data;
    } catch (error) {
      const msg = error?.response?.data?.message;
      toast.warn(msg ? `Order Not Created: ${[].concat(msg).join(', ')}` : 'Order Not Created');
    }
    return null;
  };
  const getPhysicalStatusOptions = async (orderId) => {
    try {
      const response = await apiService.get(`${PHYSICAL_API.statuses}/orders/${orderId}`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
      });
      return response.data;
    } catch (error) {
      toast.warn('Could not load allowed statuses');
      return null;
    }
  };
  const updatePhysicalOrder = async (orderId, changes) => {
    try {
      const { data } = await apiService.patch(
        `${PHYSICAL_API.orders}/${orderId}`,
        emptyToNull(changes),
        { headers: authHeaders() }
      );
      toast.success('Order updated!');
      return data;
    } catch (error) {
      toast.error(physicalErrorMessage(error, 'Error updating order!'));
      throw error;
    }
  };
  const deletePhysicalOrder = async (orderId) => {
    try {
      await apiService.delete(`${PHYSICAL_API.orders}/${orderId}`, { headers: authHeaders() });
      toast.success('Order deleted!');
      return true;
    } catch (error) {
      toast.error(physicalErrorMessage(error, 'Error deleting order!'));
      return false;
    }
  };
  const changePhysicalStatuses = async (payload) => {
    try {
      const { data } = await apiService.post(PHYSICAL_API.statusesBulk, payload, {
        headers: authHeaders(),
      });
      toast.success(`${data.updated} parcel(s) moved to ${data.status}`);
      return data;
    } catch (error) {
      toast.error(physicalErrorMessage(error, 'Status not changed'));
      throw error;
    }
  };
  const dispatchPhysicalOrder = async (tracking) => {
    try {
      const { data } = await apiService.post(
        PHYSICAL_API.statusesDispatch,
        { tracking },
        { headers: authHeaders() }
      );
      toast.success(`Moved to ${data.status}`);
      return data;
    } catch (error) {
      toast.error(physicalErrorMessage(error, 'Could not check order!'));
      throw error;
    }
  };
  const addPhysicalParcelToShelf = async ({ tracking, shelf_id: shelfCode }) => {
    try {
      const { data } = await apiService.patch(
        `${PHYSICAL_API.shelves}/${encodeURIComponent(shelfCode)}/parcels`,
        { tracking },
        { headers: authHeaders() }
      );
      getShelves();
      getPalletes();
      toast.success('Order Updated!');
      playAudio('success.wav');
      return data;
    } catch (error) {
      toast.error(physicalErrorMessage(error));
      playAudio('error.wav');
    }
    return undefined;
  };
  const addPhysicalParcelToPallet = async (palletId, tracking) => {
    try {
      const { data } = await apiService.patch(
        `${PHYSICAL_API.pallets}/${palletId}/parcels`,
        { tracking },
        { headers: authHeaders() }
      );
      getPalletes();
      toast.success('Order Updated!');
      return data;
    } catch (error) {
      toast.error(physicalErrorMessage(error));
    }
    return undefined;
  };
  const changePhysicalStatus = async (orderId, status, options = {}) => {
    try {
      const response = await apiService.patch(
        `${PHYSICAL_API.statuses}/orders/${orderId}`,
        { status, ...withoutEmptyValues(options) },
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`,
          },
        }
      );
      return response.data;
    } catch (error) {
      const msg = error?.response?.data?.message;
      toast.error(msg ? [].concat(msg).join(', ') : 'Status not changed');
      throw error;
    }
  };
  const checkPhysicalLimits = async (parcels, direction) => {
    try {
      const response = await apiService.post(
        PHYSICAL_API.ordersLimitCheck,
        withoutEmptyValues({ direction, parcels }),
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`,
          },
        }
      );
      return response.data ?? [];
    } catch (error) {
      toast.warn('Could not check weight / value limits');
      return [];
    }
  };
  const getRSOrders = async (id) => {
    try {
      const response = await apiService.post(
        `rs/findPallets`,
        {
          palletIds: id,
        },
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`,
          },
        }
      );
      const data = await response.data;
      return data; // Success path
    } catch (error) {
      console.error('Error getting RS orders:', error);
      if (error.response && error.response.status === 401) {
        localStorage.removeItem('token');
        redirect('/');
      }
      return null; // Error path
    }
  };
  // Personal RS download flow - calls a different endpoint with the same payload
  const getRSOrdersPersonal = async (id) => {
    try {
      const response = await apiService.post(
        PHYSICAL_API.rsExport,
        {
          palletIds: id,
        },
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`,
          },
        }
      );
      const data = await response.data;
      return data;
    } catch (error) {
      console.error('Error getting RS orders (personal):', error);
      if (error.response && error.response.status === 401) {
        localStorage.removeItem('token');
        redirect('/');
      }
      return null;
    }
  };
  const sendFile = async (files) => {
    if (files.length === 0) {
      toast.warn('No file selected for upload');
      return;
    }

    const file = files[0]; // Assuming you're uploading the first file
    const formData = new FormData();
    formData.append('file', file);

    try {
      // Adding progress tracking and increased timeout
      // eslint-disable-next-line
      const response = await apiService.post(`rs/upload`, formData, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
        timeout: 300000, // Increased timeout to 5 minutes (300 seconds)
        onUploadProgress: (progressEvent) => {
          const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          console.log(`Upload progress: ${percentCompleted}%`);
        },
      });

      // Notify user of successful upload
      toast.success('File sent successfully!');
    } catch (error) {
      if (error.code === 'ECONNABORTED') {
        console.error('Error: Request timed out');
        toast.error('File upload timed out!');
      } else if (error.message === 'canceled') {
        console.error('Upload was canceled:', error);
        toast.error('File upload was canceled!');
      }
      if (error.response.data.message) {
        toast.error(error.response.data.message, error.response.data.message);
      } else {
        console.error('Error sending file:', error);
        toast.error('Error sending file!');
      }
    }
  };

  const sendFilePhysical = async (files) => {
    if (files.length === 0) {
      toast.warn('No file selected for upload');
      return;
    }

    const file = files[0]; // Assuming you're uploading the first file
    const formData = new FormData();
    formData.append('file', file);

    try {
      // Adding progress tracking and increased timeout
      // eslint-disable-next-line
      const response = await apiService.post(PHYSICAL_API.rsUpload, formData, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
        timeout: 300000, // Increased timeout to 5 minutes (300 seconds)
        onUploadProgress: (progressEvent) => {
          const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          console.log(`Upload progress: ${percentCompleted}%`);
        },
      });

      // Notify user of successful upload
      toast.success('Physical orders file sent successfully!');
    } catch (error) {
      if (error.code === 'ECONNABORTED') {
        console.error('Error: Request timed out');
        toast.error('File upload timed out!');
      } else if (error.message === 'canceled') {
        console.error('Upload was canceled:', error);
        toast.error('File upload was canceled!');
      }
      if (error.response?.data?.message) {
        toast.error(error.response.data.message, error.response.data.message);
      } else {
        console.error('Error sending file:', error);
        toast.error('Error sending file!');
      }
    }
  };

  const getCourse = async () => {
    try {
      const response = await apiService.get(`/exchange-rate`, {
        timeout: 20000,
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
      });
      const data = await response.data['BOG course'];
      setCourse(data);
      return data;
    } catch (error) {
      setCourse('');
      return undefined;
    }
  };

  const getGroupCalculatedPrices = async (groupId) => {
    try {
      const response = await apiService.get(`${PHYSICAL_API.payments}/groups/${groupId}/prices`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
      });
      const data = await response.data;
      return data;
    } catch (error) {
      console.error('Error fetching group calculated prices:', error);
      toast.error('Error fetching calculated prices!');
      return null;
    }
  };
  useEffect(() => {
    getOrderss();
    getOrderssPhysical();
    getOrdersForCheck();
    getCourse();
    getOrderTimeSlot();
  }, []);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const createOrderCallback = useCallback(createOrder, []);
  const createPhysicalOrderCallback = useCallback(createPhysicalOrder, []);
  const checkPhysicalLimitsCallback = useCallback(checkPhysicalLimits, []);
  const getPhysicalStatusOptionsCallback = useCallback(getPhysicalStatusOptions, []);
  const changePhysicalStatusCallback = useCallback(changePhysicalStatus, []);
  const changePhysicalStatusesCallback = useCallback(changePhysicalStatuses, []);
  const updatePhysicalOrderCallback = useCallback(updatePhysicalOrder, []);
  const deletePhysicalOrderCallback = useCallback(deletePhysicalOrder, []);
  const dispatchPhysicalOrderCallback = useCallback(dispatchPhysicalOrder, []);
  const addPhysicalParcelToShelfCallback = useCallback(addPhysicalParcelToShelf, [getShelves, getPalletes]);
  const addPhysicalParcelToPalletCallback = useCallback(addPhysicalParcelToPallet, [getPalletes]);
  const deleteOrderCallback = useCallback(deleteOrder, []);
  const updateOrderCallback = useCallback(updateOrder, [updateOrder]);
  const checkAndDeliverOrderCallback = useCallback(checkAndDeliverOrder, []);
  const updatedDeclarationCallback = useCallback(updateDeclaration, []);
  const createDeclarationCallback = useCallback(createDeclarationDB, [createDeclarationDB]);
  const updateOrderByBoxAndPalletCallback = useCallback(updateOrderByBoxAndPallet, [
    getBoxes,
    getPalletes,
  ]);
  const updateGroupCallback = useCallback(updateGroup, []);
  const getCustomFeeSumCallback = useCallback(getCustomFeeSum, []);
  const payCustomFeesCallback = useCallback(payCustomFees, []);
  const getQuickshipperExcelDataCallback = useCallback(getQuickshipperExcelData, []);
  const getOrderssCallback = useCallback(getOrderss, []);
  const getOrderssPhysicalCallback = useCallback(getOrderssPhysical, []);
  const getOrdersForCheckCallback = useCallback(getOrdersForCheck, []);
  const getOrderByIdCallback = useCallback(getOrderById, []);
  const getPhysicalOrderByIdCallback = useCallback(getPhysicalOrderById, []);
  const sendToDeliveryCallback = useCallback(sendToDelivery, []);
  const rejectPaymentCallback = useCallback(rejectPayment, []);
  const updateOrderByShelfCallback = useCallback(updateOrderByShelf, [getShelves, getPalletes]);
  const updateOrderStatusCallback = useCallback(updateOrderStatus, []);
  const getOrderByTrackingCallBack = useCallback(getOrderByTracking, []);
  const createOrderWithStepsCallback = useCallback(createOrderWithSteps, []);
  const getRSOrdersCallBack = useCallback(getRSOrders, []);
  const getRSOrdersPersonalCallBack = useCallback(getRSOrdersPersonal, []);
  const sendFileCallback = useCallback(sendFile, []);
  const sendFilePhysicalCallback = useCallback(sendFilePhysical, []);
  const getGroupCalculatedPricesCallback = useCallback(getGroupCalculatedPrices, []);

  const getOrderTimeSlotCallback = useCallback(getOrderTimeSlot, []);
  const getPhysicalOrdersByGroupIdCallback = useCallback(getPhysicalOrdersByGroupId, []);
  const getOrdersByReceiverPhoneCallback = useCallback(getOrdersByReceiverPhone, []);
  const orderActionsValue = useMemo(
    () => ({
      createOrder: createOrderCallback,
      deleteOrder: deleteOrderCallback,
      updateOrder: updateOrderCallback,
      checkAndDeliverOrder: checkAndDeliverOrderCallback,
      getOrderss: getOrderssCallback,
      getOrderById: getOrderByIdCallback,
      getPhysicalOrderById: getPhysicalOrderByIdCallback,
      sendToDelivery: sendToDeliveryCallback,
      rejectPayment: rejectPaymentCallback,
      getOrdersForCheck: getOrdersForCheckCallback,
      updateOrderByBoxAndPallet: updateOrderByBoxAndPalletCallback,
      updateOrderByShelf: updateOrderByShelfCallback,
      updateOrderStatus: updateOrderStatusCallback,
      getOrderByTracking: getOrderByTrackingCallBack,
      createOrderWithSteps: createOrderWithStepsCallback,
      getRSOrders: getRSOrdersCallBack,
      getRSOrdersPersonal: getRSOrdersPersonalCallBack,
      sendFile: sendFileCallback,
      sendFilePhysical: sendFilePhysicalCallback,
      updateDeclaration: updatedDeclarationCallback,
      createDeclarationDB: createDeclarationCallback,
      createPhysicalOrder: createPhysicalOrderCallback,
      checkPhysicalLimits: checkPhysicalLimitsCallback,
      getPhysicalStatusOptions: getPhysicalStatusOptionsCallback,
      changePhysicalStatus: changePhysicalStatusCallback,
      changePhysicalStatuses: changePhysicalStatusesCallback,
      updatePhysicalOrder: updatePhysicalOrderCallback,
      deletePhysicalOrder: deletePhysicalOrderCallback,
      dispatchPhysicalOrder: dispatchPhysicalOrderCallback,
      addPhysicalParcelToShelf: addPhysicalParcelToShelfCallback,
      addPhysicalParcelToPallet: addPhysicalParcelToPalletCallback,
      getOrderssPhysical: getOrderssPhysicalCallback,
      getOrderTimeSlot: getOrderTimeSlotCallback,
      getPhysicalOrdersByGroupId: getPhysicalOrdersByGroupIdCallback,
      getOrdersByReceiverPhone: getOrdersByReceiverPhoneCallback,
      updateGroup: updateGroupCallback,
      getGroupCalculatedPrices: getGroupCalculatedPricesCallback,
      getCustomFeeSum: getCustomFeeSumCallback,
      payCustomFees: payCustomFeesCallback,
      getQuickshipperExcelData: getQuickshipperExcelDataCallback,
    }),
    [
      createOrderCallback,
      deleteOrderCallback,
      updateOrderCallback,
      checkAndDeliverOrderCallback,
      getOrderssCallback,
      getOrderByIdCallback,
      getPhysicalOrderByIdCallback,
      sendToDeliveryCallback,
      rejectPaymentCallback,
      updateOrderByBoxAndPalletCallback,
      updateOrderByShelfCallback,
      updateOrderStatusCallback,
      getOrdersForCheckCallback,
      getOrderByTrackingCallBack,
      createOrderWithStepsCallback,
      getRSOrdersCallBack,
      getRSOrdersPersonalCallBack,
      sendFileCallback,
      sendFilePhysicalCallback,
      updatedDeclarationCallback,
      createDeclarationCallback,
      createPhysicalOrderCallback,
      checkPhysicalLimitsCallback,
      getPhysicalStatusOptionsCallback,
      changePhysicalStatusCallback,
      changePhysicalStatusesCallback,
      updatePhysicalOrderCallback,
      deletePhysicalOrderCallback,
      dispatchPhysicalOrderCallback,
      addPhysicalParcelToShelfCallback,
      addPhysicalParcelToPalletCallback,
      getOrderssPhysicalCallback,
      getOrderTimeSlotCallback,
      getPhysicalOrdersByGroupIdCallback,
      getOrdersByReceiverPhoneCallback,
      updateGroupCallback,
      getGroupCalculatedPricesCallback,
      getCustomFeeSumCallback,
      payCustomFeesCallback,
      getQuickshipperExcelDataCallback,
    ]
  );

  const ordersValue = useMemo(
    () => ({
      orders,
      ordersCheck,
      course,
      ordersTimeSlot,
      orderRecordDetails,
      physicalOrders,
      physicalOrdersDetails,
      physicalCheckoutOrders,
    }),
    [
      orders,
      ordersCheck,
      course,
      ordersTimeSlot,
      orderRecordDetails,
      physicalOrders,
      physicalOrdersDetails,
      physicalCheckoutOrders,
    ]
  );
  return (
    <OrderContext.Provider value={ordersValue}>
      <OrderActionsContext.Provider value={orderActionsValue}>
        {children}
      </OrderActionsContext.Provider>
    </OrderContext.Provider>
  );
};

export const useOrderData = () => useContext(OrderContext);
export const useOrderActions = () => useContext(OrderActionsContext);

OrderProvider.propTypes = {
  children: PropTypes.element,
};
